import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChallengeFindingDto } from '../types/dtos/challenge';
import { challengeFindingService } from '../services/challenge-finding.service';

const FINDINGS_KEY = 'challenge-findings';

export const useChallengeFindings = (
  organizationId: string,
  challengeId: string,
  isActive?: boolean
) => {
  return useQuery({
    queryKey: [FINDINGS_KEY, organizationId, challengeId, { isActive }],
    queryFn: () =>
      challengeFindingService
        .findAll(organizationId, challengeId, isActive)
        .then((res) => res.data),
    enabled: !!organizationId && !!challengeId,
  });
};

export const useChallengeFinding = (
  organizationId: string,
  challengeId: string,
  id: string
) => {
  return useQuery({
    queryKey: [FINDINGS_KEY, organizationId, challengeId, id],
    queryFn: () =>
      challengeFindingService
        .findById(organizationId, challengeId, id)
        .then((res) => res.data),
    enabled: !!organizationId && !!challengeId && !!id,
  });
};

export const useCreateChallengeFinding = (
  organizationId: string,
  challengeId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (finding: ChallengeFindingDto) =>
      challengeFindingService
        .create(organizationId, challengeId, finding)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, challengeId],
      });
    },
  });
};

export const useUpdateChallengeFinding = (
  organizationId: string,
  challengeId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, finding }: { id: string; finding: ChallengeFindingDto }) =>
      challengeFindingService
        .update(organizationId, challengeId, id, finding)
        .then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, challengeId],
      });
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, challengeId, id],
      });
    },
  });
};

export const useDeleteChallengeFinding = (
  organizationId: string,
  challengeId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      challengeFindingService
        .delete(organizationId, challengeId, id)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, challengeId],
      });
    },
  });
}; 